import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, FolderKanban, Mail, Pencil, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { spaceOf } from "@/lib/spaces";
import { fmtDate, fromNow } from "@/lib/time";
import { priorityOf, scheduleText } from "@/lib/priority";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { TicketUpdates } from "@/components/ticket/TicketUpdates";
import { TicketAi } from "@/components/ticket/TicketAi";
import { Checklist } from "@/components/ticket/Checklist";
import { useSettings } from "@/context/SettingsContext";

const Meta = ({ r }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border bg-background/40 p-4 text-sm">
    <div><dt className="text-xs text-muted-foreground">Next email</dt><dd className="font-mono" data-testid="ticket-next-email">{r.status === "done" ? "Done" : r.next_fire_at ? fmtDate(r.next_fire_at) : "Sent"}</dd></div>
    <div><dt className="text-xs text-muted-foreground">Emails sent</dt><dd className="flex items-center gap-1 font-mono"><Mail className="h-3.5 w-3.5" /> {r.sent_count || 0}</dd></div>
    <div><dt className="text-xs text-muted-foreground">Set by</dt><dd>{spaceOf(r.created_by).name} · {fromNow(r.created_at)}</dd></div>
    <div><dt className="text-xs text-muted-foreground">Project</dt><dd>{r.project_title ? <Link to={`/projects/${r.project_id}`} className="flex items-center gap-1 hover:underline"><FolderKanban className="h-3.5 w-3.5" /> {r.project_title}</Link> : "–"}</dd></div>
  </dl>
);

export const ReminderTicket = () => {
  const { ticketId, closeTicket, version, run, openEdit } = useApp();
  const { celebrate } = useSettings();
  const [r, setR] = useState(null);

  useEffect(() => {
    if (!ticketId) return setR(null);
    api.get(`/reminders/${ticketId}`).then((x) => setR(x.data)).catch(() => closeTicket());
    return undefined;
  }, [ticketId, version, closeTicket]);

  const done = r?.status === "done";
  const toggle = () => run(() => api.patch(`/reminders/${r.id}`, { status: done ? "pending" : "done" }), done ? "Reopened" : "Marked done").then((ok) => ok && !done && celebrate(r.title));
  const p = priorityOf(r?.priority);

  return (
    <Sheet open={!!ticketId} onOpenChange={(o) => !o && closeTicket()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-white/10 bg-card sm:max-w-xl" data-testid="reminder-ticket">
        {!r ? <div className="mt-10 h-32 animate-pulse rounded-xl bg-white/[0.04]" /> : (
          <div className="space-y-6 pt-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold" style={{ background: `${p.color}1f`, color: p.color }} data-testid="ticket-priority">
                  <span className="h-2 w-2 rounded-full" style={{ background: p.color }} /> {scheduleText(r)}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><SpaceAvatar space={r.space} /> {spaceOf(r.space).name}'s space</span>
              </div>
              <SheetTitle className={`mt-3 font-display text-3xl font-bold tracking-tight ${done ? "line-through opacity-60" : ""}`} data-testid="ticket-title">{r.title}</SheetTitle>
              <SheetDescription className="mt-2 whitespace-pre-wrap">{r.details || "No details."}</SheetDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={toggle} data-testid="ticket-toggle-done" className={`flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors ${done ? "border hover:bg-white/5" : "bg-emerald-400 text-black hover:bg-emerald-300"}`}>
                {done ? <><RotateCcw className="h-4 w-4" /> Reopen</> : <><Check className="h-4 w-4" /> Mark done</>}
              </button>
              <button onClick={() => openEdit("reminder", r)} data-testid="ticket-edit" className="flex h-9 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors hover:bg-white/5">
                <Pencil className="h-4 w-4" /> Edit
              </button>
            </div>
            <Meta r={r} />
            <TicketAi key={r.id} r={r} />
            <Checklist r={r} />
            <TicketUpdates r={r} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
